import { Icon } from '@susu/ui/components'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useLabel, usePluralLabel } from '../../i18n'

interface OrderCardProps {
  imageUrl?: string
  status?: string
  hasCustomMadeIcon?: boolean
  hasMultipleProducts?: boolean
  title: string
  orderNumber: string
  itemCount: number
  price: string
  accountNumber?: string
  onOrderClick?: (orderId: string) => void
}

export function OrderCard({
  imageUrl,
  hasCustomMadeIcon = false,
  hasMultipleProducts = false,
  title,
  orderNumber,
  itemCount,
  price,
  accountNumber,
  onOrderClick,
}: OrderCardProps) {
  const [imageError, setImageError] = useState(false)
  const navigate = useNavigate()

  const handleClick = () => {
    // If a callback is provided (embedded mode), use it instead of routing
    if (onOrderClick) {
      onOrderClick(orderNumber)
      return
    }

    // Otherwise, use router navigation (standalone mode)
    const currentParams = new URLSearchParams(window.location.search)
    const params = new URLSearchParams({
      orderId: orderNumber,
    })
    if (accountNumber) {
      params.set('accountNumber', accountNumber)
    }
    // Preserve token if it exists
    const token = currentParams.get('token')
    if (token) {
      params.set('token', token)
    }
    navigate(`/order-details?${params.toString()}`)
  }

  const t = useLabel()
  const tPlural = usePluralLabel()

  return (
    <button
      className="flex w-full items-center gap-16 bg-white hover:bg-gray-50 transition-colors text-left"
      type="button"
      onClick={handleClick}
      data-testid="order-card"
    >
      {/* Product Image */}
      <div className="relative flex w-[90px] h-[108px] md:w-[130px] md:h-[156px] flex-col items-center justify-center">
        {hasMultipleProducts && (
          <div className="absolute inset-0 -rotate-[5deg] overflow-hidden rounded-md border border-border bg-background-gray-dark" />
        )}

        <div className="relative flex p-10 md:p-16 w-[90px] h-[108px] md:w-[130px] md:h-[156px] flex-col items-center justify-center overflow-hidden rounded-md border border-border bg-background-gray-dark">
          {!imageError && imageUrl ? (
            <img
              src={imageUrl}
              alt={t('orderList.list.product')}
              loading="lazy"
              decoding="async"
              onError={() => setImageError(true)}
              className="w-full h-full flex-shrink-0 object-contain"
            />
          ) : (
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>{t('orderList.item.noImage')}</title>
              <rect width="40" height="40" rx="4" fill="#E5E5E5" />
              <path d="M12 28L17 23L21 27L26 22L28 24V28H12Z" fill="#C6C6C6" />
              <circle cx="24" cy="16" r="3" fill="#C6C6C6" />
            </svg>
          )}
        </div>

        {hasCustomMadeIcon && (
          <div className="backdrop-blur-sm absolute top-[6px] left-[6px] flex h-[22px] w-[22px] items-center justify-center rounded-md bg-white-alpha-600">
            <Icon name={'custom-made'} size={14} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex min-h-[108px] md:min-h-[156px] flex-1 flex-col justify-center gap-6">
        {/* TODO: Uncomment when all status fields are available from API */}
        {/* {status && (
          <div className="self-start rounded-md bg-surface-alt py-2 px-8">
            <span className="text-xs font-light leading-xs text-text block">
              {status}
            </span>
          </div>
        )} */}

        <div className="flex flex-col items-start gap-4">
          <h3
            className="text-base font-medium leading-base text-text"
            data-testid="order-date"
          >
            {title}
          </h3>

          <div className="flex items-center gap-8 text-sm font-light leading-sm text-text">
            <span data-testid="order-number">{orderNumber}</span>
            <span className="w-1 h-[11px] bg-background-gray-dark" />
            <span data-testid="order-item-count">
              {tPlural('orderList.list.itemCount', itemCount)}
            </span>
            <span className="w-1 h-[11px] bg-background-gray-dark" />
            <span data-testid="order-total">{price}</span>
          </div>
        </div>
      </div>
    </button>
  )
}
